import { sum } from 'lodash'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import type { ColorMap } from '~/components/cms/ChargerStatus'

import type { ChargerModel, LatestConnectorStatusSummaryModel } from '../api/client/chargers'

export const CurrentConnectorStatuses = [
  'Available',
  'Reserved',
  'Preparing',
  'Charging',
  'SuspendedEV',
  'SuspendedEVSE',
  'Finishing',
  'Faulted',
  'Unavailable',
  'Unknown',
  'Offline'
] as const

export function isConnectorStatus(status?: string): status is CurrentConnectorStatus {
  return CurrentConnectorStatuses.includes(status as CurrentConnectorStatus)
}

export type CurrentConnectorStatus = (typeof CurrentConnectorStatuses)[number]
export type LastKnownConnectorStatus = Exclude<CurrentConnectorStatus, 'Offline'>

export const OperationalStatuses = ['Operative', 'Inoperative'] as const
export type OperationalStatus = (typeof OperationalStatuses)[number]

export const ConnectivityStatuses = ['Online', 'Offline'] as const
export type ConnectivityStatus = (typeof ConnectivityStatuses)[number]

export const ChargerStatuses = [
  'Operative',
  'Faulted',
  'PartiallyFaulted',
  'Unavailable',
  'Offline',
  'Unknown'
] as const

export function isChargerStatus(status?: string): status is CurrentChargerStatus {
  return ChargerStatuses.includes(status as CurrentChargerStatus)
}

export type CurrentChargerStatus = (typeof ChargerStatuses)[number]
export type LastKnownChargerStatus = Exclude<CurrentChargerStatus, 'Offline'>

export const getDisplayedChargerStatus = (charger?: ChargerModel): CurrentChargerStatus =>
  charger?.latestChargerStatus.currentCombinedStatus ?? 'Unknown'

/**
 * Derive the overall charger status from connector statuses.
 * This aggregates multiple connector statuses into a single representative status
 * for the charger as a whole.
 */
export function deriveChargerStatus(charger: ChargerModel): CurrentConnectorStatus {
  const statusEntries = Object.entries(charger.connectorStatus).filter(([key]) => key !== '0')
  const statuses = new Set(statusEntries.map(([, value]) => value.currentCombinedStatus))

  // If all connectors have the same status, return that status
  if (statuses.size === 1) return (statuses.values().next().value ?? 'Unknown') as CurrentConnectorStatus

  // Check for override conditions based on charger-level status
  if (statuses.has('Unknown') || charger.latestChargerStatus.status === 'Unknown') return 'Unknown'
  if (charger.latestChargerStatus.connectivityStatus === 'Offline') return 'Offline'

  // Priority-based status derivation when multiple connector statuses exist
  if (statuses.has('Available')) return 'Available'
  if (statuses.has('Charging') || statuses.has('Preparing') || statuses.has('Finishing')) return 'Charging'
  if (statuses.has('Faulted')) return 'Faulted'
  if (statuses.has('Unavailable')) return 'Unavailable'
  if (statuses.has('Reserved')) return 'Reserved'
  if (statuses.has('SuspendedEVSE')) return 'SuspendedEVSE'
  if (statuses.has('SuspendedEV')) return 'SuspendedEV'

  return 'Unknown'
}

export const statusIsOperative = (status?: CurrentConnectorStatus): boolean => {
  return !!status && !['Faulted', 'Unavailable', 'Offline'].includes(status)
}

export const statusIsPreparingOrCharging = (status?: CurrentConnectorStatus): boolean => {
  return !!status && ['Preparing', 'Charging'].includes(status)
}

export const transactionActiveStatuses: ReadonlySet<string> = new Set([
  'charging',
  'finishing',
  'suspendedev',
  'suspendedevse'
])

/** The connector statuses that say a vehicle is on the cable, so the connector's last transaction is its session. */
export const VEHICLE_CONNECTED_STATUSES = ['Charging', 'Finishing', 'SuspendedEV', 'SuspendedEVSE']

export const inUseStatuses: CurrentConnectorStatus[] = [
  'Preparing',
  'Charging',
  'SuspendedEVSE',
  'SuspendedEV',
  'Finishing'
]

export function statusIsInUse(status?: CurrentConnectorStatus): boolean {
  return inUseStatuses.includes(status ?? 'Unknown')
}

export function numStatusesInUse(statusSummary?: LatestConnectorStatusSummaryModel) {
  const currentStatuses = statusSummary?.currentStatuses ?? {}

  return Object.entries(currentStatuses).reduce((acc, [status, count]) => {
    const isInUse = statusIsInUse(status as CurrentConnectorStatus)

    return isInUse ? acc + count : acc
  }, 0)
}

export function renderStatus(status: string): string {
  switch (status) {
    case 'PartiallyFaulted':
      return 'Partially Faulted'
    case 'SuspendedEV':
      return 'Suspended EV'
    default:
      return status
  }
}

export function useConnectorStatusFormatter() {
  const t = useTranslations('data.chargers')
  return useCallback(
    (status?: string) => {
      const lookupKey = isConnectorStatus(status) || isChargerStatus(status) ? status : 'Unknown'
      return t(`statuses_${lookupKey}`)
    },
    [t]
  )
}

type StatusSummaryMap = Record<string, number>
type StatusSummaryParseArgs = {
  colorMap: ColorMap<string>
  statuses?: StatusSummaryMap
}

export function useStatusSummaries({ colorMap, statuses }: StatusSummaryParseArgs) {
  const formatConnectorStatus = useConnectorStatusFormatter()
  return useMemo(() => {
    if (!statuses) return []

    const total = sum(Object.values(statuses ?? []))
    if (total === 0) return []

    return Object.entries(statuses)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        label: formatConnectorStatus(status),
        value: (count / total) * 100,
        color: colorMap[status] ?? '#000000'
      }))
  }, [colorMap, formatConnectorStatus, statuses])
}
